// Definitionen der Berechtigungen für verschiedene Rollen
export const ROLE_PERMISSIONS = {
  'Admin': {
    canViewAll: true,
    canEditAll: true,
    canDeleteAll: true,
    canCreateAll: true
  },
  'Reader': {
    canViewAll: true,
    canEditAll: false,
    canDeleteAll: false,
    canCreateAll: false
  },
  'User Admin': {
    canViewUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canCreateUsers: true
  },
  'Firm Admin': {
    canViewFirms: true,
    canEditFirms: true,
    canDeleteFirms: true,
    canCreateFirms: true
  },
  'Event Admin': {
    canViewEvents: true,
    canEditEvents: true,
    canDeleteEvents: true,
    canCreateEvents: true
  },
  'Event Report': {
    canViewEventReports: true,
    canEditEventReports: false,
    canDeleteEventReports: false,
    canCreateEventReports: false
  },
  'User Role Admin': {
    canViewUserRoles: true,
    canEditUserRoles: true,
    canDeleteUserRoles: false,
    canCreateUserRoles: false
  },
  'Location Admin': {
    canViewLocations: true,
    canEditLocations: true,
    canDeleteLocations: true,
    canCreateLocations: true
  },
  'Lunch-Roulette Admin': {
    canViewLunchRoulette: true,
    canEditLunchRoulette: true,
    canDeleteLunchRoulette: true,
    canCreateLunchRoulette: true
  },
  'Log Admin': {
    canViewLogs: true,
    canEditLogs: false,
    canDeleteLogs: true,
    canCreateLogs: false
  },
  'Job Admin': {
    canViewJobs: true,
    canEditJobs: true,
    canDeleteJobs: true,
    canCreateJobs: true
  },
  'Role Admin': {
    canViewRoles: true,
    canEditRoles: true,
    canDeleteRoles: true,
    canCreateRoles: true
  }
} as const;

export type RoleName = keyof typeof ROLE_PERMISSIONS;
export type Permission = keyof typeof ROLE_PERMISSIONS[RoleName];

// Helper-Funktionen für Berechtigungsprüfungen
export const hasPermission = (userRoles: string[], permission: string): boolean => {
  // Admin hat immer alle Berechtigungen
  if (userRoles.includes('Admin')) {
    return true;
  }

  // Prüfe spezifische Berechtigungen für jede Rolle
  return userRoles.some(role => {
    const rolePermissions = ROLE_PERMISSIONS[role as RoleName];
    if (!rolePermissions) return false;
    
    return rolePermissions[permission as Permission] === true;
  });
};

export const hasAnyRole = (userRoles: string[], requiredRoles: string[]): boolean => {
  return requiredRoles.some(role => userRoles.includes(role));
};

export const canAccessPage = (userRoles: string[], page: string): boolean => {
  // Admin kann auf alle Seiten zugreifen
  if (userRoles.includes('Admin')) {
    return true;
  }

  switch (page) {
    case 'users':
      return hasAnyRole(userRoles, ['User Admin', 'User Role Admin']);
    case 'firms':
      return hasAnyRole(userRoles, ['Firm Admin']);
    case 'events':
      return hasAnyRole(userRoles, ['Event Admin', 'Event Report']);
    case 'locations':
      return hasAnyRole(userRoles, ['Location Admin']);
    case 'roles':
      return hasAnyRole(userRoles, ['Role Admin', 'User Role Admin']);
    case 'errors':
      return hasAnyRole(userRoles, ['Error Admin']);
    case 'jobs':
      return hasAnyRole(userRoles, ['Job Admin', 'Log Admin']);
    case 'job-logs':
      return hasAnyRole(userRoles, ['Log Admin']);
    case 'api-keys':
      return hasAnyRole(userRoles, ['API Admin']);
    case 'lunch-roulette':
      return hasAnyRole(userRoles, ['Lunch-Roulette Admin']);
    default:
      return false;
  }
};

// Hilfsfunktion für UI-Elemente (Buttons, etc.)
export const canPerformAction = (userRoles: string[], action: 'create' | 'edit' | 'delete' | 'view', context: string): boolean => {
  // Admin kann alles
  if (userRoles.includes('Admin')) {
    return true;
  }

  // Reader kann nur anschauen
  if (userRoles.includes('Reader') && action !== 'view') {
    return false;
  }

  // Spezifische Berechtigungen basierend auf Kontext
  const permissionKey = `can${action.charAt(0).toUpperCase() + action.slice(1)}${context.charAt(0).toUpperCase() + context.slice(1)}`;
  return hasPermission(userRoles, permissionKey);
};
